import type { Selection } from '../urlState'

/**
 * SIDE-BY-SIDE CONVERSATIONS ON A WIDE SCREEN — owner, 2026-08-22: 在寬螢幕的狀態下,
 * 讓使用者可以從 Side Panel 將一個元件 (Agent / Project Chat / Friend 1-1 Chat) 使用
 * 拖拉的方式拉到主顯示欄位, 並且選擇位置 (左, 右), 造成雙開或多開的能力.
 *
 * He is already doing this with two browser windows — the screenshot has app-dev open
 * twice, avg-latency on the left and avg-devenv on the right. This brings it inside the
 * app.
 *
 * THE GEOMETRY AND THE LIST LIVE HERE, alone, because they are the part that can be
 * judged without a browser: where a drop lands, how many panes are allowed, what a
 * width permits. The rendering is the caller's.
 *
 * WIDTH IS A HARD GATE, not a preference. Two conversations in 800px is two unusable
 * ones — the app's own rail is 208px and a chat column stops being readable below about
 * 420. A phone gets exactly what it has today, and `canSplit` says so rather than the
 * layout discovering it.
 */

/** Below this, one pane. The rail plus two readable columns do not fit under it. */
export const MIN_SPLIT_WIDTH = 1100
/**
 * Each pane needs at least this much to be worth having.
 *
 * Not exported: it is an input to `maxPanes`, and a caller doing its own arithmetic
 * with it would be a second place that decides how many panes fit.
 */
const MIN_PANE_WIDTH = 420

export type Pane = {
  /** Stable across re-orders, so React keys and focus survive a drag. */
  id: string
  sel: Selection
  /**
   * Terminal or Clean View, for THIS pane — owner, 2026-08-22: 視窗應該要記住當前的
   * Terminal / Clean View 的狀態, 這樣在雙開的時候不會跑掉.
   *
   * Recorded so a pane recreated after a reload comes back in the mode somebody chose
   * for it. While the pane is alive its own URL is the truth; this is the seed.
   */
  view?: string | null
}

/** Can this viewport hold more than one conversation at all? */
export function canSplit(width: number): boolean {
  return width >= MIN_SPLIT_WIDTH
}

/**
 * How many panes this width can hold.
 *
 * Derived rather than a constant: the answer on a 1440px laptop and a 3840px desktop
 * are different, and a fixed cap would either waste the big screen or crush the small
 * one. The rail is subtracted first because it is always there.
 */
export function maxPanes(width: number, railWidth = 208): number {
  if (!canSplit(width)) return 1
  return Math.max(1, Math.floor((width - railWidth) / MIN_PANE_WIDTH))
}

/** Where a pointer at `x` inside a pane's box wants the new pane to go. */
export type DropSide = 'left' | 'right'

/**
 * Which half of the box the pointer is in.
 *
 * A DEAD ZONE in the middle would be the obvious refinement and is deliberately absent:
 * every drop has to mean something, and a drop that does nothing is indistinguishable
 * from a drag the app failed to notice. The halves meet exactly in the middle.
 */
export function dropSide(x: number, boxLeft: number, boxWidth: number): DropSide {
  return x - boxLeft < boxWidth / 2 ? 'left' : 'right'
}

/** Do two selections point at the same conversation? */
export function sameSelection(a: Selection, b: Selection): boolean {
  if (!a || !b) return a === b
  if (a.kind !== b.kind) return false
  if (a.kind === 'project' && b.kind === 'project') return a.id === b.id
  if (a.kind === 'dm' && b.kind === 'dm') return (a.peer ?? a.key) === (b.peer ?? b.key)
  if (a.kind === 'agent' && b.kind === 'agent') {
    if (a.hash && b.hash) return a.hash === b.hash && (a.device ?? null) === (b.device ?? null)
    return a.session === b.session && (a.host ?? null) === b.host
  }
  return false
}

/**
 * Put `sel` beside the pane it was dropped on, or move it if it is already open.
 *
 * ALREADY-OPEN MOVES RATHER THAN DUPLICATES. Two panes on one conversation would be two
 * views of the same transcript, each polling, each with its own scroll — and the second
 * one is never what somebody meant by dragging. Dropping a conversation that is already
 * on screen relocates it, which is the other thing they could have meant.
 */
export function placePane(
  panes: Pane[],
  sel: Selection,
  onPaneId: string,
  side: DropSide,
  newId: string,
  limit: number,
): Pane[] {
  if (!sel) return panes
  const existing = panes.findIndex((p) => sameSelection(p.sel, sel))
  const without = existing >= 0 ? panes.filter((_, i) => i !== existing) : panes
  const target = without.findIndex((p) => p.id === onPaneId)
  // A drop on a pane that has just gone (a race with a close) appends rather than
  // vanishing: the person asked for this conversation to be on screen.
  const at = target < 0 ? without.length : side === 'left' ? target : target + 1
  const moved: Pane = existing >= 0 ? panes[existing]! : { id: newId, sel }
  const next = [...without.slice(0, at), moved, ...without.slice(at)]
  /*
   * Over the limit, the OLDEST other pane goes — not the one just dropped, and not the
   * one it was dropped next to. Both of those are what the person is looking at.
   */
  if (next.length <= limit) return next
  const drop = next.findIndex((p) => p.id !== moved.id && p.id !== onPaneId)
  return drop < 0 ? next.slice(0, limit) : next.filter((_, i) => i !== drop)
}

/** Close one pane. The last one never closes — the app would have nothing to show. */
export function closePane(panes: Pane[], id: string): Pane[] {
  if (panes.length <= 1) return panes
  const next = panes.filter((p) => p.id !== id)
  return next.length ? next : panes
}

/**
 * Fold the layout back to one pane, keeping `keepId` if it is still there.
 *
 * A window narrowed below the gate must not silently drop what somebody was reading, so
 * the pane they last touched survives and the rest are closed rather than hidden — a
 * hidden pane that reappears on resize is state nobody can see and everybody forgets.
 */
export function foldToOne(panes: Pane[], keepId: string | null): Pane[] {
  if (panes.length <= 1) return panes
  const keep = panes.find((p) => p.id === keepId) ?? panes[0]!
  return [keep]
}

/* ── the layout survives a reload ─────────────────────────────────────────── */

const STORE = 'wt_panes'

/**
 * Kept on the DEVICE rather than in the URL.
 *
 * A layout is about this screen — how wide it is, how many conversations fit on it —
 * and the URL is about a conversation, which is the thing people paste to each other.
 * Putting three panes in a link would make every shared link open somebody else's
 * workspace on a screen that may not fit it.
 */
export function loadPanes(): Pane[] {
  try {
    const raw = window.localStorage.getItem(STORE)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((p): p is Pane => !!p && typeof p === 'object' && typeof (p as Pane).id === 'string')
      .filter((p) => !!p.sel)
  } catch {
    return []
  }
}

export function savePanes(panes: Pane[]): void {
  try {
    // One pane is not a layout — it is the app. Storing it would make "close the split"
    // and "never split"two different states that look identical.
    if (panes.length <= 1) window.localStorage.removeItem(STORE)
    else window.localStorage.setItem(STORE, JSON.stringify(panes))
  } catch {
    /* storage unavailable — the layout is a convenience, not the app */
  }
}
